import Session from "users/session/session";
import VendorRepresentativeAPIRequest from "work/entities/entity-vendor-representative/api/request-contracts/vendor-representative-api-request";
import EntityVendorRepresentative from "work/entities/entity-vendor-representative/entity-vendor-representative";
import ProposalReviewerAPIRequest, { ProposalReviewerAPIRequestPayload } from "work/entities/proposal/api/request-contracts/proposal-reviewer-api-request";
import ReplaceableDocumentAPIRequest, { ReplaceableDocumentAPIRequestPayload } from "work/entities/proposal/api/request-contracts/replaceable-document-api-request";
import WorkFeeScheduleCategoryAPIRequest, { WorkFeeScheduleCategoryAPIRequestPayload } from "work/entities/proposal/api/request-contracts/work-fee-schedule-category-api-request";
import WorkTeamAPIRequest, { WorkTeamAPIRequestPayload } from "work/entities/proposal/api/request-contracts/work-team-api-request";
import Proposal from "work/entities/proposal/proposal";
import {
  Masked
} from "work/entities/work-agreement/work-agreement";
import EntityMemberAPIRequest from "work/values/entity-member/api/request-contracts/entity-member-api-request";

export type WorkProposalAPIRequestPayload = {
  RFPId?: string;
  creatorType?: string;
  creatorEntityId?: string;
  name?: string;
  description?: string;
  clientTeam?: WorkTeamAPIRequestPayload;
  vendorTeam?: WorkTeamAPIRequestPayload;
  feeSchedule?: WorkFeeScheduleCategoryAPIRequestPayload[];
  startDate?: string;
  endDate?: string;
  discount?: number;
  negotiable?: boolean;
  responseDueBy?: string;
  supersedesId?: string;
  clientReviewers?: ProposalReviewerAPIRequestPayload[];
  vendorReviewers?: ProposalReviewerAPIRequestPayload[];
  clientPolicyDocuments?: ReplaceableDocumentAPIRequestPayload[];
  vendorPolicyDocuments?: ReplaceableDocumentAPIRequestPayload[];
  conflictsCheckWaived?: boolean | null;
  teamRestricted?: boolean | null;
  conflictsDocuments?: ReplaceableDocumentAPIRequestPayload[];
  clientTeamTemplateIds?: string[]; 
  vendorTeamTemplateIds?: string[];
  clientFeeScheduleTemplateIds?: string[];
  vendorFeeScheduleTemplateIds?: string[];
  redlining?: object;
}

export default class WorkProposalAPIRequest {
  RFPId?: string;
  creatorType?: string;
  creatorEntityId?: string;
  name?: string;
  description?: string;
  clientTeam?: WorkTeamAPIRequest;
  vendorTeam?: WorkTeamAPIRequest;
  feeSchedule?: WorkFeeScheduleCategoryAPIRequest[];
  startDate?: string;
  endDate?: string;
  discount?: number;
  negotiable?: boolean;
  responseDueBy?: string;
  supersedesId?: string;
  clientReviewers?: ProposalReviewerAPIRequest[];
  vendorReviewers?: ProposalReviewerAPIRequest[];
  clientPolicyDocuments?: ReplaceableDocumentAPIRequest[];
  vendorPolicyDocuments?: ReplaceableDocumentAPIRequest[];
  conflictsCheckWaived?: boolean | null;
  teamRestricted?: boolean | null;
  conflictsDocuments?: ReplaceableDocumentAPIRequest[];
  clientTeamTemplateIds?: string[];
  vendorTeamTemplateIds?: string[];
  clientFeeScheduleTemplateIds?: string[];
  vendorFeeScheduleTemplateIds?: string[];
  redlining?: object;

  constructor(proposal: Proposal, session: Readonly<Session>) {
    this.RFPId = proposal.RFPId?.value;
    this.name = proposal.name?.value;
    this.description = proposal.description?.value;
    this.vendorTeam = Masked.isPresentNotMasked(proposal.vendorTeam)
      ? Object.assign(
        new WorkTeamAPIRequest(),
        {
          leader: proposal.vendorTeam.leader?.entityId
            ? new VendorRepresentativeAPIRequest(
              new EntityVendorRepresentative(
                proposal.vendorTeam.leader.userId,
                proposal.vendorTeam.leader.entityId
              )
            )
            : undefined,
          members: proposal.vendorTeam.members.map((member) =>
            Object.assign(new EntityMemberAPIRequest(), {
              entityId: member.entityId?.value,
              userId: member.userId?.value
            }).payload),
          marketplaceId: proposal.vendorTeam.marketplaceId?.value,
        }
      )
      : undefined;
    this.feeSchedule = Masked.isNotMasked(proposal.feeSchedule) ?
      proposal.feeSchedule.map((category) => {
        return new WorkFeeScheduleCategoryAPIRequest(category);
      }) : undefined;
    this.discount = Masked.isPresentNotMasked(proposal.discount) ? proposal.discount.numeral : undefined;
    this.creatorType = session.accountType;
    this.creatorEntityId = session.currentEntity.entityId.value;
    this.startDate = proposal.startDate?.toISOString();
    this.endDate = proposal.endDate?.toISOString();
    this.responseDueBy = proposal.responseDueBy?.value.toISOString();
    this.negotiable = proposal.negotiable;
    this.clientReviewers = proposal.clientReviewers.map((reviewer) => {
      return Object.assign(new ProposalReviewerAPIRequest(reviewer));
    });
    this.vendorReviewers = proposal.vendorReviewers.map((reviewer) => {
      return Object.assign(new ProposalReviewerAPIRequest(reviewer));
    });
    this.supersedesId = proposal.supersedes?.id?.value;
    this.clientTeam = Object.assign(new WorkTeamAPIRequest(), {
      leader: proposal.client?.userId
        ? new VendorRepresentativeAPIRequest(
          new EntityVendorRepresentative(
            proposal.client?.userId,
            proposal.client?.entityId
          )
        )
        : undefined
    });
    this.clientPolicyDocuments = Masked.isNotMasked(proposal.clientPolicyDocuments) ?
      proposal.clientPolicyDocuments.map(
        (document) => {
          return Object.assign(new ReplaceableDocumentAPIRequest(document));
        }
      ) : undefined;
    this.vendorPolicyDocuments = Masked.isNotMasked(proposal.vendorPolicyDocuments) ?
      proposal.vendorPolicyDocuments.map(
        (document) => {
          return Object.assign(new ReplaceableDocumentAPIRequest(document));
        }
      ) : undefined;

    this.conflictsCheckWaived = Masked.isPresentNotMasked(proposal.conflictsCheckWaived) ?
      proposal.conflictsCheckWaived.value : undefined;

    this.teamRestricted = Masked.isPresentNotMasked(proposal.teamRestricted) ? proposal.teamRestricted.value : undefined;

    this.conflictsDocuments = Masked.isPresentNotMasked(proposal.conflictsDocuments) ?
      proposal.conflictsDocuments.map((document) => {
        return Object.assign(new ReplaceableDocumentAPIRequest(document));
      }) : undefined;

    this.clientTeamTemplateIds = proposal.clientTeamTemplateIds.map(
      (id) => id.value
    );
    this.vendorTeamTemplateIds = proposal.vendorTeamTemplateIds.map(
      (id) => id.value
    );
    this.clientFeeScheduleTemplateIds =
      proposal.clientFeeScheduleTemplateIds.map((id) => id.value);
    this.vendorFeeScheduleTemplateIds =
      proposal.vendorFeeScheduleTemplateIds.map((id) => id.value);
    this.redlining = proposal.redline?.toJSON();
  }

  public get payload(): WorkProposalAPIRequestPayload {
    return {
      RFPId: this.RFPId,
      creatorType: this.creatorType,
      creatorEntityId: this.creatorEntityId,
      name: this.name,
      description: this.description,
      clientTeam: this.clientTeam?.payload,
      teamRestricted: this.teamRestricted,
      vendorTeam: this.vendorTeam?.payload,
      feeSchedule: this.feeSchedule?.map((category) => category.payload),
      startDate: this.startDate,
      endDate: this.endDate,
      discount: this.discount,
      negotiable: this.negotiable,
      responseDueBy: this.responseDueBy,
      supersedesId: this.supersedesId,
      clientReviewers: this.clientReviewers?.map(
        (reviewer) => reviewer.payload
      ),
      vendorReviewers: this.vendorReviewers?.map(
        (reviewer) => reviewer.payload
      ),
      clientFeeScheduleTemplateIds: this.clientFeeScheduleTemplateIds,
      vendorFeeScheduleTemplateIds: this.vendorFeeScheduleTemplateIds,
      clientTeamTemplateIds: this.clientTeamTemplateIds,
      vendorTeamTemplateIds: this.vendorTeamTemplateIds,
      clientPolicyDocuments: this.clientPolicyDocuments,
      vendorPolicyDocuments: this.vendorPolicyDocuments,
      conflictsCheckWaived: this.conflictsCheckWaived,
      conflictsDocuments: this.conflictsDocuments,
      redlining: this.redlining
    };
  }
}
